import React, { useState, useEffect } from 'react';
import { 
    Activity, 
    ShieldAlert, 
    Database, 
    School, 
    Users, 
    Calendar, 
    Terminal, 
    Cpu, 
    Layers, 
    RefreshCcw, 
    Server, 
    AlertTriangle 
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui';

const SuperAdminDashboard: React.FC = () => {
    const { user } = useAuth();
    const [currentTime, setCurrentTime] = useState(new Date());
    const [queryCounter, setQueryCounter] = useState(41295);
    const [systemLoad, setSystemLoad] = useState(12);

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        
        // Simulate real-time read/write activity counter
        const counterTimer = setInterval(() => {
            setQueryCounter(prev => prev + Math.floor(Math.random() * 4) + 1);
            setSystemLoad(Math.floor(Math.random() * 15) + 8);
        }, 3000);

        return () => {
            clearInterval(timer);
            clearInterval(counterTimer);
        };
    }, []);

    const metrics = [
        {
            name: 'Connected Campuses',
            value: '4 Branches',
            status: 'All Synced',
            color: 'text-emerald-400',
            icon: School,
            glow: 'rgba(16,185,129,0.1)'
        },
        {
            name: 'Active Database Connection',
            value: 'Online',
            status: 'Active Firestore',
            color: 'text-teal-400',
            icon: Database,
            glow: 'rgba(20,184,166,0.1)'
        },
        {
            name: 'Real-Time Sync Queue',
            value: `${queryCounter.toLocaleString()} Ops`,
            status: '0 Latency',
            color: 'text-cyan-400',
            icon: Activity,
            glow: 'rgba(6,182,212,0.1)'
        },
        {
            name: 'System Core Integrity',
            value: 'SECURE',
            status: '99.98% Healthy',
            color: 'text-emerald-500',
            icon: Cpu,
            glow: 'rgba(16,185,129,0.1)'
        }
    ];

    const auditLogs = [
        { id: 1, action: 'ROOT_AUTH_SUCCESS', user: 'superadmin@edulink.com', ip: '192.168.1.1', status: 'SUCCESS', time: '12 seconds ago' },
        { id: 2, action: 'FETCH_CAMPUS_CONFIGURATION', user: 'superadmin@edulink.com', ip: '192.168.1.1', status: 'SUCCESS', time: '3 minutes ago' },
        { id: 3, action: 'WRITE_BRANCH_TIMING', user: 'admin_branch_02@edulink.com', ip: '10.0.4.15', status: 'SUCCESS', time: '12 minutes ago' },
        { id: 4, action: 'UPDATE_RFID_CARD_STREAM', user: 'rfid_daemon_clerk@edulink.com', ip: '182.91.4.92', status: 'ACCEPTED', time: '28 minutes ago' },
        { id: 5, action: 'USER_ROLE_ESCALATION_CHECK', user: 'hr_clerk@edulink.com', ip: '10.0.4.19', status: 'DENIED', time: '1 hour ago' }
    ];

    const branches = [
        { name: 'Noor-e-Hamza Central Campus', code: 'NH-CENTRAL', users: 512, status: 'Active', latency: '12ms' },
        { name: 'Noor-e-Hamza Gulberg Branch', code: 'NH-GULBERG', users: 284, status: 'Active', latency: '19ms' },
        { name: 'Noor-e-Hamza Defense Campus', code: 'NH-DEFENSE', users: 318, status: 'Active', latency: '15ms' },
        { name: 'Noor-e-Hamza Johar Town', code: 'NH-JOHAR', users: 120, status: 'Active', latency: '22ms' }
    ];

    return (
        <div className="space-y-6 text-slate-100 min-h-screen bg-slate-950 p-6 rounded-3xl border border-emerald-500/10 shadow-[0_20px_50px_rgba(0,0,0,0.6)] relative overflow-hidden">
            {/* Ambient Background Matrix Overlay */}
            <div className="absolute inset-0 opacity-5 pointer-events-none"
                 style={{
                     backgroundImage: 'radial-gradient(circle at 1px 1px, #10b981 1.5px, transparent 0)',
                     backgroundSize: '16px 16px'
                 }}>
            </div>

            {/* Dashboard Header Bar */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between pb-6 border-b border-emerald-500/20 relative z-10 space-y-4 md:space-y-0">
                <div>
                    <div className="flex items-center space-x-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-ping"></span>
                        <h2 className="text-2xl font-black tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 uppercase">
                            Root Gateway Dashboard
                        </h2>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 font-semibold">
                        Global School Management System Core Controller & System Integrity Center
                    </p>
                </div>
                <div className="flex items-center space-x-3 bg-slate-900 border border-emerald-500/20 px-4 py-2 rounded-2xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)]">
                    <Terminal className="h-4.5 w-4.5 text-emerald-400 animate-pulse" />
                    <span className="text-xs font-bold text-slate-300 font-mono tracking-wider">
                        {currentTime.toLocaleTimeString()}
                    </span>
                    <span className="text-[10px] font-black uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 rounded">
                        SYS: {systemLoad}%
                    </span>
                </div>
            </div>

            {/* Metrics cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
                {metrics.map((m, index) => (
                    <div 
                        key={index} 
                        className="bg-slate-900/60 border border-emerald-500/20 rounded-2xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.3)] hover:border-emerald-500/40 hover:shadow-[0_0_20px_rgba(16,185,129,0.1)] transition-all duration-300 group"
                    >
                        <div className="flex items-center justify-between">
                            <div className="space-y-1">
                                <p className="text-[10px] font-black uppercase text-slate-500 tracking-wider">{m.name}</p>
                                <p className={`text-2xl font-black ${m.color} tracking-wide`}>{m.value}</p>
                                <p className="text-[10px] text-slate-400 font-bold">{m.status}</p>
                            </div>
                            <div 
                                className="p-3.5 rounded-xl bg-slate-950 border border-emerald-500/20 group-hover:border-emerald-400 transition-colors duration-300 shadow-[inset_0_2px_4px_rgba(0,0,0,0.4)]"
                                style={{ boxShadow: `0 0 15px ${m.glow}` }}
                            >
                                <m.icon className={`h-6 w-6 ${m.color} animate-pulse`} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Split screen content logs/actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative z-10">
                
                {/* Branch Operations Status */}
                <div className="bg-slate-900/40 border border-emerald-500/20 rounded-2xl p-6 lg:col-span-2">
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center space-x-2">
                            <Layers className="h-5 w-5 text-emerald-400" />
                            <h3 className="text-sm font-black uppercase tracking-wider text-slate-200">Global Campus Node Roster</h3>
                        </div>
                        <span className="text-[9px] font-black uppercase px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg">
                            4 Active
                        </span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-emerald-500/20">
                                    <th className="pb-3 text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">Branch Details</th>
                                    <th className="pb-3 text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">ID Code</th>
                                    <th className="pb-3 text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">Synced Headcount</th>
                                    <th className="pb-3 text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">Network Latency</th>
                                    <th className="pb-3 text-[10px] font-extrabold uppercase text-slate-400 tracking-wider">Operational Mode</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-emerald-500/10">
                                {branches.map((b, i) => (
                                    <tr key={i} className="hover:bg-slate-900/50 transition-colors duration-150">
                                        <td className="py-3.5 text-xs font-bold text-slate-200">{b.name}</td>
                                        <td className="py-3.5 text-xs font-mono text-emerald-400">{b.code}</td>
                                        <td className="py-3.5 text-xs text-slate-300 font-semibold">{b.users} Active Users</td>
                                        <td className="py-3.5 text-xs font-mono text-cyan-400">{b.latency}</td>
                                        <td className="py-3.5">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[9px] font-black uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                {b.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Cyber System controls panel */}
                <div className="bg-slate-900/40 border border-emerald-500/20 rounded-2xl p-6">
                    <div className="flex items-center space-x-2 mb-5">
                        <Server className="h-5 w-5 text-teal-400" />
                        <h3 className="text-sm font-black uppercase tracking-wider text-slate-200">Global Core Controllers</h3>
                    </div>

                    <div className="space-y-4">
                        <div className="p-4 bg-slate-950/80 border border-emerald-500/20 rounded-xl flex items-center justify-between">
                            <div>
                                <h4 className="text-xs font-bold text-slate-200">System Force Sync</h4>
                                <p className="text-[9px] text-slate-500 mt-0.5">Flush memory caches and hard sync with cloud</p>
                            </div>
                            <button className="p-2 bg-emerald-500 text-slate-950 rounded-xl hover:bg-emerald-400 transition-colors duration-200 shadow-[0_0_12px_rgba(16,185,129,0.3)]">
                                <RefreshCcw className="h-4 w-4 stroke-[2.5px]" />
                            </button>
                        </div>

                        <div className="p-4 bg-slate-950/80 border border-emerald-500/20 rounded-xl flex items-center justify-between">
                            <div>
                                <h4 className="text-xs font-bold text-slate-200">System Logs Export</h4>
                                <p className="text-[9px] text-slate-500 mt-0.5">Generate system-wide decrypted audit CSV</p>
                            </div>
                            <button className="p-2 bg-teal-500 text-slate-950 rounded-xl hover:bg-teal-400 transition-colors duration-200 shadow-[0_0_12px_rgba(20,184,166,0.3)]">
                                <Database className="h-4 w-4 stroke-[2.5px]" />
                            </button>
                        </div>

                        <div className="p-4 bg-red-950/20 border border-red-500/30 rounded-xl flex items-center justify-between">
                            <div>
                                <h4 className="text-xs font-bold text-red-300">Global Lockdown Mode</h4>
                                <p className="text-[9px] text-red-400/60 mt-0.5">Immediately freeze login panel credentials</p>
                            </div>
                            <button className="p-2 bg-red-600 text-white rounded-xl hover:bg-red-500 transition-colors duration-200 shadow-[0_0_12px_rgba(239,68,68,0.3)]">
                                <AlertTriangle className="h-4 w-4 stroke-[2.5px]" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Database audit log visual console */}
            <div className="bg-slate-900/40 border border-emerald-500/20 rounded-2xl p-6 relative z-10">
                <div className="flex items-center space-x-2 mb-4">
                    <Terminal className="h-5 w-5 text-emerald-400" />
                    <h3 className="text-sm font-black uppercase tracking-wider text-slate-200">Security Gate Access Terminal Logs</h3>
                </div>

                <div className="bg-slate-950/90 rounded-xl p-4 border border-emerald-500/10 font-mono text-[11px] leading-relaxed text-slate-300 max-h-[220px] overflow-y-auto custom-scrollbar">
                    {auditLogs.map((log) => (
                        <div key={log.id} className="py-1 flex items-start space-x-3 border-b border-slate-900/50">
                            <span className="text-emerald-500 font-extrabold">[{log.time}]</span>
                            <span className="text-slate-400">{log.action}</span>
                            <span className="text-slate-500">USER: {log.user}</span>
                            <span className="text-slate-500">IP: {log.ip}</span>
                            <span className={`px-1.5 py-0.2 rounded font-black text-[9px] ${
                                log.status === 'DENIED' 
                                    ? 'bg-red-950/60 text-red-400 border border-red-500/30' 
                                    : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                            }`}>
                                {log.status}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SuperAdminDashboard;
